/**
 * Loads the signed-in user's display preferences once, applies them app-wide,
 * and shares them through context.
 *
 * **Why a provider and not just a hook.** Two reasons, both learned the hard
 * way:
 *
 * 1. Preferences have to apply on *every* page, not only on the settings page.
 *    A hook called from one screen only runs while that screen is mounted, so
 *    the theme would snap back the moment you navigated away.
 * 2. Mounted once, there is exactly one fetch. Two components each calling a
 *    fetching hook would each fetch.
 *
 * **Saving is automatic.** Appearance preferences apply the moment they are
 * picked, so a Save button was both redundant and actively misleading — the
 * change had already happened, and the button sat there implying it had not.
 * `save()` debounces, so dragging through options costs one request, not one
 * per option.
 *
 * **The load effect depends on `isAuthenticated` and nothing else — deliberately.**
 * `useAccessToken()` returns a `useCallback` over `[instance, accounts,
 * config.scopes]`, and `accounts`/`scopes` get fresh identities on most renders,
 * so `getToken` is a *new function* nearly every render. An effect that lists it
 * as a dependency **and** sets state in its body re-runs forever: effect →
 * setState → render → new `getToken` → effect → … It did, at roughly one request
 * every 6ms. Live-changing values are read through refs instead, so the effect's
 * dependency list stays honest *and* small.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  DEFAULT_SETTINGS,
  getMySettings,
  putMySettings,
  type SettingsOut,
} from "@/shared/api/settings";

const CACHE_KEY = "daquma.settings";

/** Font stacks per allow-list value. Each ends in a generic family so a missing
 *  webfont degrades to something readable rather than to the browser default. */
const FONT_STACKS: Record<string, string> = {
  system:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  inter: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
};

/** Multiplier applied to every Mantine text size. Bounded, so the UI cannot be
 *  driven to an unusable size and leave the user unable to find the control
 *  that would undo it. */
const FONT_SCALES: Record<string, number> = {
  sm: 0.9,
  md: 1,
  lg: 1.15,
  xl: 1.3,
};

/** Mantine's own defaults, in rem, for every variable that sizes text.
 *
 *  Scaling text means rewriting **all** of these. An earlier attempt set only
 *  the theme's `fontSizes.md`, which maps to `--mantine-font-size-md` alone —
 *  and nearly every component in this app renders at `sm` or `xs`, so the text
 *  did not change size. What *did* change were the line-heights derived from
 *  it, which is why the only visible effect was text drifting further apart.
 *
 *  `--mantine-scale` is deliberately left alone: it scales spacing, padding and
 *  component dimensions too, which is browser-zoom, not a text-size setting. */
const TEXT_VARS: Record<string, number> = {
  "--mantine-font-size-xs": 0.75,
  "--mantine-font-size-sm": 0.875,
  "--mantine-font-size-md": 1,
  "--mantine-font-size-lg": 1.125,
  "--mantine-font-size-xl": 1.25,
  "--mantine-h1-font-size": 2.125,
  "--mantine-h2-font-size": 1.625,
  "--mantine-h3-font-size": 1.375,
  "--mantine-h4-font-size": 1.125,
  "--mantine-h5-font-size": 1,
  "--mantine-h6-font-size": 0.875,
};

function readCache(): SettingsOut {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<SettingsOut>) };
  } catch {
    // Private windows, cleared storage, or a browser blocking site data. The
    // defaults are always a correct answer, so this never needs to surface.
    return DEFAULT_SETTINGS;
  }
}

function writeCache(settings: SettingsOut): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Caching is an optimisation; failing to cache is not worth reporting.
  }
}

function same(a: SettingsOut, b: SettingsOut): boolean {
  return a.theme === b.theme && a.font_size === b.font_size && a.font_family === b.font_family;
}

/** Apply typography to the document. The colour scheme is Mantine's business. */
function applyTypography(settings: SettingsOut): void {
  const root = document.documentElement;
  root.style.setProperty("--daquma-font-family", FONT_STACKS[settings.font_family] ?? "");

  const scale = FONT_SCALES[settings.font_size] ?? 1;
  for (const [name, rem] of Object.entries(TEXT_VARS)) {
    // Rounded: 0.75 * 1.15 is 0.8624999999999999 in binary floating point, and
    // emitting that into CSS is both ugly and awkward to assert on.
    const size = parseFloat((rem * scale).toFixed(5));
    // Keep the `* var(--mantine-scale)` factor Mantine's own definitions carry,
    // so anything that relies on scale (and any future zoom control) still
    // composes with this instead of being overwritten by it.
    root.style.setProperty(name, `calc(${size}rem * var(--mantine-scale))`);
  }
}

/** How long to wait after the last change before persisting. Long enough that
 *  clicking through four font sizes is one request; short enough that it lands
 *  before anyone navigates away. */
const SAVE_DEBOUNCE_MS = 600;

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface UserSettingsState {
  settings: SettingsOut;
  loading: boolean;
  /** What the last save is doing, for a quiet status line. There is no Save
   *  button: appearance applies on pick and persists on its own. */
  status: SaveStatus;
  /** Apply immediately and persist (debounced). */
  update: (next: SettingsOut) => void;
}

const UserSettingsContext = createContext<UserSettingsState>({
  settings: DEFAULT_SETTINGS,
  loading: false,
  status: "idle",
  update: () => {},
});

export function UserSettingsProvider({ children }: PropsWithChildren) {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [settings, setSettings] = useState<SettingsOut>(readCache);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read through refs inside the effect so its dependency list can stay
  // `[isAuthenticated]` — see the module docstring for what happens otherwise.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const apply = useCallback((next: SettingsOut) => {
    applyTypography(next);
    // Light only (product decision 2026-10-02): the saved `theme` is ignored.
    // Only re-render when something actually changed. Setting a fresh object
    // every time would re-render on each apply, which is what turned a loose
    // dependency into a runaway loop rather than a one-off extra render.
    setSettings((current) => (same(current, next) ? current : next));
  }, []);

  useEffect(() => {
    apply(readCache());
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getTokenRef.current();
        const fetched = await getMySettings(token);
        if (cancelled) return;
        apply(fetched);
        writeCache(fetched);
      } catch {
        // A failed fetch leaves the cached/default appearance in place.
        // Blocking the app over "the font might be wrong" is out of proportion.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, apply]);

  // Apply now, persist shortly. The apply is what the user sees, so it must not
  // wait on the network; the write is bookkeeping and can coalesce.
  const update = useCallback(
    (next: SettingsOut) => {
      apply(next);
      writeCache(next);
      setStatus("saving");
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void (async () => {
          try {
            const token = await getTokenRef.current();
            const stored = await putMySettings(token, next);
            apply(stored);
            writeCache(stored);
            setStatus("saved");
          } catch {
            // The appearance already changed locally and stays changed; only
            // the "follows you to another device" half failed. Say so in the
            // status line rather than throwing a modal over a font choice.
            setStatus("error");
          }
        })();
      }, SAVE_DEBOUNCE_MS);
    },
    [apply],
  );

  // A pending save must not outlive the provider.
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const value = useMemo<UserSettingsState>(
    () => ({ settings, loading, status, update }),
    [settings, loading, status, update],
  );

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}

/** The current user's appearance preferences. Requires `UserSettingsProvider`
 *  above it; without one it returns defaults rather than throwing, so an
 *  isolated component render in a test is not a crash. */
export function useUserSettings(): UserSettingsState {
  return useContext(UserSettingsContext);
}
